import React, { useState } from "react";
import { API_URL } from "../config";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogContent,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaArrowRight,
} from "react-icons/fa";

const EASE = [0.16, 1, 0.3, 1];

const NewSeasonModal = ({ token, onClose, open }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState(false);

  console.log(error, showPass);
  const handleConfirm = async () => {
    if (!password.trim()) {
      setError("Password is required.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/roundup-season/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token?.access}`,
        },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("✅ New season started. Students have been moved forward.");
        onClose();
      } else {
        setError(data?.detail || "Failed to start new season. Try again.");
      }
    } catch (err) {
      setError("Connection error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setPassword("");
    setShowPass(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(8px)", background: "rgba(12,26,46,0.55)" },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.95, y: 12, filter: "blur(4px)" }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <DialogContent sx={{ p: 0, overflow: "visible" }}>
              <Box
                sx={{
                  background: "rgba(255,255,255,0.96)",
                  backdropFilter: "blur(24px)",
                  borderRadius: 3.5,
                  border: "1px solid rgba(56,142,221,0.18)", // ← blue border
                  boxShadow:
                    "0 24px 80px rgba(56,142,221,0.12), 0 4px 24px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                {/* ── Top accent bar ───────────── */}
                <Box
                  sx={{
                    height: 3,
                    background:
                      "linear-gradient(90deg, #1D9E75, #378ADD, #1D9E75)", // ← teal→blue
                  }}
                />

                {/* ── Close button ─────────────── */}
                <Box
                  sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}
                >
                  <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    size="small"
                    sx={{
                      background: "rgba(0,0,0,0.05)",
                      color: "#94a3b8",
                      "&:hover": {
                        background: "rgba(56,142,221,0.08)",
                        color: "#378ADD",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    <FaTimes size={13} />
                  </IconButton>
                </Box>

                <Box sx={{ px: 3.5, pt: 3.5, pb: 3 }}>
                  {/* ── Icon + heading ────────────── */}
                  <Stack
                    alignItems="center"
                    sx={{ mb: 3, textAlign: "center" }}
                  >
                    <Box sx={{ position: "relative", mb: 2 }}>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: -8,
                          borderRadius: "50%",
                          border: "1px solid rgba(56,142,221,0.2)",
                          animation: "ripple 2s ease-out infinite",
                          "@keyframes ripple": {
                            "0%": { transform: "scale(0.9)", opacity: 0.6 },
                            "100%": { transform: "scale(1.35)", opacity: 0 },
                          },
                        }}
                      />
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, rgba(56,142,221,0.12), rgba(29,158,117,0.08))",
                          border: "1.5px solid rgba(56,142,221,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 8px 24px rgba(56,142,221,0.15)",
                        }}
                      >
                        {/* swap FaArrowRight or any forward icon here */}
                        <FaArrowRight size={22} color="#378ADD" />
                      </Box>
                    </Box>

                    <Chip
                      label="New class session"
                      size="small"
                      sx={{
                        mb: 1.2,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        background: "rgba(56,142,221,0.08)",
                        color: "#378ADD",
                        border: "1px solid rgba(56,142,221,0.2)",
                        letterSpacing: "0.3px",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        color: "#0c1a2e",
                        letterSpacing: "-0.3px",
                        mb: 0.6,
                      }}
                    >
                      Start next season
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#64748b", lineHeight: 1.7, maxWidth: 290 }}
                    >
                      This will{" "}
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#378ADD" }}
                      >
                        move all students
                      </Box>{" "}
                      to a new class session and reset their scores, so you can
                      begin fresh with the same group.
                    </Typography>
                  </Stack>

                  {/* ── Info box ─────────────────── */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.6,
                      mb: 2.5,
                      borderRadius: 2,
                      background: "rgba(56,142,221,0.05)",
                      border: "1px solid rgba(56,142,221,0.15)",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <FaInfoCircle
                      size={12}
                      color="#378ADD"
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#185FA5",
                        fontWeight: 600,
                        lineHeight: 1.6,
                      }}
                    >
                      Student records are preserved and carried forward. Only
                      scores and progress for the current session will be reset
                      for the new term.
                    </Typography>
                  </Box>

                  {/* ── Password field ────────────── */}
                  {/* ... same as before but replace red with blue (#378ADD / rgba(56,142,221,...)) */}

                  <Box sx={{ mb: error ? 1.5 : 2.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748b",
                        display: "block",
                        mb: 0.8,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Enter your password to continue
                    </Typography>
                    <TextField
                      fullWidth
                      type={showPass ? "text" : "password"}
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      disabled={loading}
                      onKeyDown={(e) =>
                        e.key === "Enter" && password && handleConfirm()
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaLock
                              size={14}
                              color={focused ? "#ef4444" : "#94a3b8"}
                              style={{ transition: "color 0.3s" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPass((p) => !p)}
                              edge="end"
                              sx={{
                                color: "#94a3b8",
                                "&:hover": { color: "#ef4444" },
                                transition: "color 0.2s",
                              }}
                            >
                              {showPass ? (
                                <FaEyeSlash size={14} />
                              ) : (
                                <FaEye size={14} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2.5,
                          background: focused
                            ? "rgba(239,68,68,0.03)"
                            : "rgba(255,255,255,0.8)",
                          transition: "all 0.3s",
                          color: "#0c1a2e",
                          "& fieldset": {
                            borderColor: error
                              ? "#ef4444"
                              : focused
                              ? "rgba(239,68,68,0.5)"
                              : "rgba(239,68,68,0.2)",
                            borderWidth: focused || error ? 2 : 1,
                            transition: "all 0.3s",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(239,68,68,0.45)",
                          },
                        },
                        "& input::placeholder": {
                          color: "#94a3b8",
                          opacity: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* ── Error message ────────────────────────────────── */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, filter: "blur(4px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.3, ease: EASE }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.2,
                            mb: 2,
                            borderRadius: 2,
                            background: "rgba(239,68,68,0.06)",
                            border: "1px solid rgba(239,68,68,0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <FaExclamationTriangle
                            size={11}
                            color="#ef4444"
                            style={{ flexShrink: 0 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#ef4444",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                            }}
                          >
                            {error}
                          </Typography>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* ── Actions ──────────────────── */}
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        fontSize: "0.88rem",
                        textTransform: "none",
                        borderColor: "rgba(56,142,221,0.25)",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#378ADD",
                          color: "#378ADD",
                          background: "rgba(56,142,221,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>

                    <motion.div
                      style={{ flex: 1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={loading || !password}
                        sx={{
                          py: 1.5,
                          borderRadius: 2.5,
                          fontWeight: 800,
                          fontSize: "0.88rem",
                          textTransform: "none",
                          background:
                            loading || !password
                              ? "rgba(56,142,221,0.4)"
                              : "linear-gradient(135deg, #378ADD, #185FA5)",
                          boxShadow: "0 6px 20px rgba(56,142,221,0.3)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #185FA5, #0C447C)",
                            boxShadow: "0 8px 28px rgba(56,142,221,0.42)",
                          },
                        }}
                      >
                        {loading ? (
                          <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                          >
                            <CircularProgress
                              size={15}
                              sx={{ color: "#fff" }}
                            />
                            <span>Starting new season…</span>
                          </Stack>
                        ) : (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <FaArrowRight size={13} />
                            <span>Advance to next season</span>
                          </Stack>
                        )}
                      </Button>
                    </motion.div>
                  </Stack>
                </Box>
              </Box>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default NewSeasonModal;
